import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, verifyAdminUser } from '../../lib/supabase';
import '../../admin.css';

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const checkExistingSession = async () => {
      try {
        const { data, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) throw sessionError;

        if (data.session?.user) {
          const isAdmin = await verifyAdminUser(data.session.user.id);
          if (!cancelled && isAdmin) {
            navigate('/admin/dashboard', { replace: true });
            return;
          }
        }
      } catch {
        // Ignore — user can still sign in manually
      } finally {
        if (!cancelled) setCheckingSession(false);
      }
    };

    const timeout = setTimeout(() => {
      if (!cancelled) setCheckingSession(false);
    }, 1500);

    checkExistingSession().finally(() => clearTimeout(timeout));

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const { data, error: authError } = await supabase.auth.signInWithPassword({ email, password });
      if (authError) throw authError;

      const isAdmin = await verifyAdminUser(data.user.id);
      if (!isAdmin) {
        await supabase.auth.signOut();
        throw new Error('This account is not authorized. Ask an admin to add you to the admins table.');
      }

      navigate('/admin/dashboard', { replace: true });
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="admin-login-page">
      <div className="admin-login-card">
        <h1>ICI CMS</h1>
        <p className="admin-login-subtitle">Sign in to manage content</p>

        {checkingSession && (
          <p className="admin-login-hint">Checking session…</p>
        )}

        <form onSubmit={handleSubmit} className="admin-login-form">
          {error && <p className="admin-login-error">{error}</p>}
          <div>
            <label htmlFor="admin-email">Email</label>
            <input
              id="admin-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </div>
          <div>
            <label htmlFor="admin-password">Password</label>
            <input
              id="admin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
          </div>
          <button type="submit" disabled={submitting}>
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
}
